import { defineStore } from 'pinia';
import { $alert } from '@nio-fe/lego';
import { md5 as JSMd5 } from 'js-md5';
import useMatestoreStore from './matestoreStore';
import type {
  UserInfoData,
  GlobalData,
  AdministratorData,
} from '@/api/model/globalModel';
import { Authority } from '@/api/model/globalModel';
import { mateBackendClient } from '@/api/client';
import { revalidateCache } from '@/utils/revalidate_cache';
import { doLogout } from '@/utils/logout_action';
let fullScreenLoadingTimeoutId: any = undefined;
let fullScreenHideLoadingTimeoutId: any = undefined;
const useGlobalStore = defineStore({
  id: 'titGlobal',
  state: () => ({
    isDoingLogout: false,
    lane: '' as string,
    theme: 'aurora-blue' as string,
    currentBrand: '' as string,
    userInfo: {} as UserInfoData,
    global: {} as GlobalData,
    administrator: {} as AdministratorData,
    authorityMapping: {} as Record<string, boolean>,
    sidebarVisible: true as boolean,
    fullScreenLoading: false as boolean,
    coreData: {} as any,
    initialized: false,
    brandAuthorityMapping: {} as Record<string, Record<string, boolean>>,
  }),
  getters: {
    getLane: (state) => state.lane,
    getTheme: (state) => state.theme,
    getUserInfo: (state) => state.userInfo,
    getGlobal: (state) => state.global,
    domainDisplayName: (state) =>
      state.userInfo.displayName ||
      state.userInfo.domainAccount ||
      state.userInfo.userName,
  },

  actions: {
    async appInit() {
      const matestoreStore = useMatestoreStore();
      const doCatchCoreDataError = (
        err: { resultCode?: string; data?: any } & Error
      ) => {
        const { resultCode = '', data = {} } = err;
        const domainAccount = data?.domain_account || '未找到用户';
        const expiredDays = data?.expired_days || '180';
        let message = ``;
        if (resultCode === 'unauthorized') {
          message = `用户登录信息失效, 请重新登录.`;
        } else if (resultCode === 'notin_auth_list') {
          message = `您(${domainAccount})没有被授权使用Mate网页版, 请联系管理员.`;
        } else if (resultCode === 'admin_disabled') {
          message = `超过${expiredDays}天未登录，账号过期，请联系总部系统运营`;
        } else {
          message = `系统关键数据获取失败, 请重新登录. 当前用户: ${domainAccount}`;
        }
        if (window?.__MATE_APP_OPTIONS?.iframeMode) {
          doLogout();
        } else {
          if (this.isDoingLogout) {
            return;
          }
          this.isDoingLogout = true;

          if (window?.__MATE_APP_OPTIONS?.iframeMode) {
            alert(message);
            doLogout();
          } else {
            $alert(message, {
              type: 'error',
            })
              .then(doLogout)
              .finally(() => {
                this.isDoingLogout = false;
              });
          }
        }
      };

      try {
        const res = await revalidateCache(
          'coreData',
          mateBackendClient.getCoreData(),
          (data) => {
            if (data) {
              if (window?.__MATE_APP_OPTIONS?.iframeMode) {
                console.warn('系统权限变更导致的页面刷新');
                window.location.reload();
              } else {
                if (this.isDoingLogout) {
                  return;
                }
                $alert('系统权限变更，页面即将刷新', {
                  type: 'info',
                  okText: '知道了',
                }).then(() => {
                  window.location.reload();
                });
              }
            }
          },
          (err) => {
            doCatchCoreDataError(err);
          },
          (data: any) => {
            const permissions = data?.brandAdmins?.map((i) => {
              const mapping = {};
              this.generateAuthorityMapping(i?.authorities || [], mapping);
              return Object.keys(mapping).sort();
            });
            return JSMd5.create()
              .update(
                JSON.stringify({
                  permissions,
                  menu: data.menu,
                  accessToken: data?.global?.accessToken,
                  brands: data.brands?.sort(),
                  accountIds: data.brandAccounts
                    ?.map((i) => i.accountId)
                    .filter((i) => !!i),
                })
              )
              .hex();
          }
        );
        this.coreData = res;
        this.generateUserInfo();
        this.global = res.global;
        matestoreStore.sourceData = res.menu;
        if (!this.hasAnyAuthority('nio') && !this.hasAnyAuthority('alps')) {
          if (window?.__MATE_APP_OPTIONS?.iframeMode) {
            alert('未配置角色，请联系业务管理员开通');
          } else {
            $alert('未配置角色，请联系业务管理员开通', {
              type: 'error',
              okText: '知道了',
            });
          }
        }
        this.initialized = true;
        return { global: this.global, lane: this.lane, env: res.info?.env };
      } catch (err: any) {
        doCatchCoreDataError(err);
      }
    },
    /**
     * @deprecated
     * @param brandCode
     * @param syncToServer
     */
    async switchBrand(brandCode: string, syncToServer = false) {
      const brandAdmin = this.coreData.brandAdmins.find(
        (i) => (i.brand = brandCode)
      );
      const brandAccount = this.coreData.brandAccounts.find(
        (i) => (i.brand = brandCode)
      );
      this.userInfo = {
        ...brandAccount,
        ...this.coreData.info,
        /*people info*/
      };

      this.administrator = {
        ...brandAdmin,
      };
      this.currentBrand = brandCode;
      if (syncToServer) {
        await mateBackendClient.switchBrand(brandCode);
      }
    },

    toggleSidebarVisibility() {
      this.sidebarVisible = !this.sidebarVisible;
    },
    getBrandAuthorityMapping(brand: string) {
      if (this.brandAuthorityMapping[brand]) {
        return this.brandAuthorityMapping[brand];
      }
      try {
        const brandAdmin = this.coreData.brandAdmins.find(
          (i) => i.brand === brand
        );
        let mapping: Record<string, boolean> = {};
        this.generateAuthorityMapping(brandAdmin?.authorities || [], mapping);
        this.brandAuthorityMapping[brand] = mapping;
        return mapping;
      } catch (e) {
        console.error('getBrandAuthorityMapping error', e);
        this.brandAuthorityMapping[brand] = {};
        return {};
      }
    },
    generateAuthorityMapping(
      authorityList: Authority[],
      mapping: Record<string, boolean> = {}
    ) {
      authorityList.forEach((i) => {
        mapping[i.identifier] = true;
        if (i.children) {
          this.generateAuthorityMapping(i.children, mapping);
        }
      });
    },
    generateUserInfo() {
      this.userInfo = {
        ...this.coreData.info,
      };
      const nioAccount = this.coreData.brandAccounts.find(
        (i) => i.brand === 'nio'
      );
      const alpsAccount = this.coreData.brandAccounts.find(
        (i) => i.brand === 'alps'
      );
      const fyAccount = this.coreData.brandAccounts.find(
        (i) => i.brand === 'fy'
      );
      this.userInfo.accountId = nioAccount?.accountId;
      this.userInfo.alpsAccountId = alpsAccount?.accountId;
      this.userInfo.fyAccountId = fyAccount?.accountId;
      this.userInfo.avatar = nioAccount?.avatar || alpsAccount?.avatar;
      this.userInfo.nickName = nioAccount?.nickname || alpsAccount?.nickname;
    },
    showFullScreenLoading(autoCloseTimeout = 0) {
      this.fullScreenLoading = true;
      if (autoCloseTimeout > 0) {
        if (fullScreenLoadingTimeoutId) {
          clearTimeout(fullScreenLoadingTimeoutId);
        }
        fullScreenLoadingTimeoutId = setTimeout(() => {
          this.fullScreenLoading = false;
        }, autoCloseTimeout);
      }
    },
    hideFullScreenLoading(delay = 0) {
      if (delay > 0) {
        if (fullScreenHideLoadingTimeoutId) {
          clearTimeout(fullScreenHideLoadingTimeoutId);
        }
        fullScreenHideLoadingTimeoutId = setTimeout(() => {
          this.fullScreenLoading = false;
        }, delay);
      } else {
        this.fullScreenLoading = false;
      }
    },
    hasAuthority(permissionKey: string, brand?: string | string[]) {
      if (!brand) {
        brand = ['nio', 'alps'];
      }
      if (Array.isArray(brand)) {
        return brand.some((i) => this.hasAuthority(permissionKey, i));
      }
      return this.getBrandAuthorityMapping(brand)
        ? this.getBrandAuthorityMapping(brand)[permissionKey]
        : false;
    },
    hasAnyAuthority(brand = 'nio') {
      return this.getBrandAuthorityMapping(brand)
        ? Object.keys(this.getBrandAuthorityMapping(brand)).length > 0
        : false;
    },
    getBrandAdministrator(brand?: string) {
      if (!brand) {
        return this.coreData.brandAdmins[0];
      }
      return this.coreData.brandAdmins.find((i) => i.brand === brand);
    },
    getAvailableBrands(): string[] {
      return this.coreData.brandAdmins.map((i) => i.brand);
    },
    getBrandsFromBrandAccounts(): string[] {
      return this.coreData?.brandAccounts?.map((i) => i.brand);
    },
    getShouldRegisterBrands() {
      const shouldRegisterBrands = this.coreData.brandAccounts
        .filter((i) => !i.accountId)
        .map((i) => i.brand);

      return shouldRegisterBrands;
    },
  },
  persist: {
    key: 'tit-app',
    storage: window.sessionStorage,
    paths: ['lane', 'theme', 'sidebarVisible'],
  },
});

export default useGlobalStore;
export type IAppStore = ReturnType<typeof useGlobalStore>;
